import React, { useCallback, useMemo, useState } from 'react';
import { uniqBy } from 'lodash';

import { useAppDispatch, useAppSelector } from 'utils/hooks';

// TODO: should not be importing this. all that logic should be in epics or a reducer
import * as arrangementAPI from 'domains/saved_arrangements/saved_arrangements.api';

import Button from 'components/shared/Button';
import Modal from 'components/shared/Modal';

import photoModule from 'domains/photos';
import multiphotos from 'domains/multiphotos';
import crops from 'domains/crops';

import { renderModal } from 'utils/modal_util';

type PhotoSelectOption = 'all' | 'none' | 'others' | 'delete';

function PhotoOptionsMenu() {
  const [confirmDialog, setConfirmDialog] = useState<React.ReactNode>(null);

  const dispatch = useAppDispatch();
  const multiphotoIds = useAppSelector(multiphotos.selectors.selectIds);
  const photos = useAppSelector(photoModule.selectors.allForActiveCollection);
  const selectedPhotoIds = useAppSelector(multiphotos.selectors.selectedPhotoIds) as number[];
  const potentialCropIds = useAppSelector(crops.selectors.potentialCropIds);
  const cropIds = useAppSelector(crops.selectors.selectIds);

  const photoIds = useMemo(() => photos.map((p) => p.id), [photos]);

  const selectAll = () => {
    if (cropIds.length > 0) {
      dispatch(multiphotos.actions.moveCropsToMultiPhotos(photoIds));
    } else {
      dispatch(multiphotos.actions.createManyMultiPhotos(photoIds));
    }
  };

  const selectInverse = () => {
    const inverse = photoIds.filter((id) => {
      return multiphotoIds.indexOf(id) === -1;
    });

    dispatch(multiphotos.actions.resetMultiPhotos());
    dispatch(multiphotos.actions.createManyMultiPhotos(inverse));
  };

  const selectNone = () => {
    dispatch(multiphotos.actions.resetMultiPhotos());
    dispatch(crops.actions.resetCrops());
  };

  const confirmMultipleDelete = useCallback(async () => {
    const linkedArrangements = await arrangementAPI.fromUploads(selectedPhotoIds);
    const uniqueArrangements = uniqBy(linkedArrangements, 'id');

    const photoNum = selectedPhotoIds.length;

    let titleText = 'You are about to delete a photo';
    let confirmationText = 'You have one photo selected.';
    let detailText = 'Would you like to delete this photo?';
    let buttonText = 'Delete Photo';
    let arrangementsText = `This photo is linked to the following arrangement${
      uniqueArrangements.length > 1 ? 's' : ''
    }:`;
    let arrangementsDeleteText = `Deleting this photo will remove it from the arrangement${
      uniqueArrangements.length > 1 ? 's' : ''
    }.`;

    if (photoNum > 1) {
      titleText = `You are about to delete ${photoNum} photos`;
      confirmationText = `You have ${photoNum} photos selected.`;
      detailText = 'Would you like to delete each selected photo?';
      buttonText = 'Delete Multiple Photos';
      arrangementsText = `Some or all of these photos are linked to the following arrangement${
        uniqueArrangements.length > 1 ? 's' : ''
      }:`;
      arrangementsDeleteText = `Deleting these photos will remove them from the arrangement${
        uniqueArrangements.length > 1 ? 's' : ''
      }.`;
    }

    const confirmDeleteButtons = (
      <React.Fragment>
        <Button
          type="confirm"
          onClick={() => {
            dispatch(photoModule.actions.removeManyPhotos.pending(selectedPhotoIds));
            setConfirmDialog(null);
          }}
        >
          {buttonText}
        </Button>
        <Button type="cancel" onClick={() => setConfirmDialog(null)}>
          Do Not Delete
        </Button>
      </React.Fragment>
    );

    setConfirmDialog(
      <Modal
        title={titleText}
        labelledBy="confirm-multiple-delete-modal"
        lastFocusedElementId="delete-multiple-photos-button"
        onClose={() => setConfirmDialog(null)}
        primaryFooterGroup={confirmDeleteButtons}
      >
        <div className="alert-message">
          <div>
            <strong>{confirmationText}</strong>
          </div>
          {uniqueArrangements.length > 0 && (
            <div className="delete-photo-arrangements">
              <p>{arrangementsText}</p>
              <ul>
                {uniqueArrangements.map(({ id, name }) => (
                  <li key={id}>{name}</li>
                ))}
              </ul>
              <p>{arrangementsDeleteText}</p>
            </div>
          )}
          <p>{detailText}</p>
        </div>
      </Modal>
    );
  }, [dispatch, selectedPhotoIds]);

  const handleSelectOption = (option: PhotoSelectOption) => {
    switch (option) {
      case 'all':
        selectAll();
        break;
      case 'others':
        selectInverse();
        break;
      case 'none':
        selectNone();
        break;
      case 'delete':
        confirmMultipleDelete();
        break;
      // no default
    }
  };

  return (
    <React.Fragment>
      <div className="photo-options-menu">
        <div className="options">
          {photoIds.length > 0 && potentialCropIds.length === 1 && multiphotoIds.length === 0 && (
            <Button
              type="icon"
              icon="check-square"
              appearance="themed"
              a11yText="Select all photos"
              size="2x"
              onClick={() => handleSelectOption('all')}
            />
          )}
          {potentialCropIds.length === 1 && multiphotoIds.length > 0 && (
            <Button
              type="icon"
              icon="times"
              appearance="themed"
              a11yText="Unselect all photos"
              size="2x"
              onClick={() => handleSelectOption('none')}
            />
          )}
          <Button
            type="icon"
            icon="trash-alt"
            className="delete-photos-button"
            id="delete-multiple-photos-button"
            onClick={() => handleSelectOption('delete')}
            appearance="themed"
            a11yText="Delete selected photos"
            size="2x"
            disabled={selectedPhotoIds.length === 0}
          />
        </div>
      </div>

      {confirmDialog && renderModal(confirmDialog)}
    </React.Fragment>
  );
}

export default PhotoOptionsMenu;
